import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { qtyDisplay } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, RefreshCw, Truck, Package2, X, CheckCircle2, ShieldCheck, ClipboardCheck, Eye, User, Mail, Phone, Cake, PawPrint, MapPin, Printer, Download } from "lucide-react";

const STATUS_BADGE = {
  vet_approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
  sent_to_fulfillment: "bg-sky-500/15 text-sky-300 border-sky-500/20",
  processing: "bg-amber-500/15 text-amber-300 border-amber-500/20",
  shipped: "bg-cyan-500/15 text-cyan-300 border-cyan-500/20",
  delivered: "bg-green-500/15 text-green-300 border-green-500/20",
  completed: "bg-green-600/15 text-green-300 border-green-600/20",
};

const NEXT_STATUS = {
  vet_approved: "processing",
  sent_to_fulfillment: "processing",
  processing: "shipped",
  shipped: "delivered",
  delivered: "completed",
};

const CARRIERS = ["", "Canada Post", "USPS", "UPS", "FedEx", "DHL"];

export default function FulfillmentDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tracking_number: "", tracking_carrier: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [details, setDetails] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/fulfillment/orders"); setOrders(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const advance = async (order, status) => {
    if (status === "shipped" && !order.tracking?.number) { setEditing(order); setForm({ tracking_number: "", tracking_carrier: "", notes: "" }); return; }
    try { await api.patch(`/fulfillment/orders/${order.order_id}/status`, { status }); toast.success(`Order marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitTracking = async (e) => {
    e.preventDefault();
    if (!form.tracking_number.trim()) { toast.error("Tracking number is required"); return; }
    setSaving(true);
    try { await api.patch(`/fulfillment/orders/${editing.order_id}/status`, { status: "shipped", ...form }); toast.success("Order shipped"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="fc-dashboard">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-white/40 mb-2">Pipeline</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Active Orders</h1>
          <p className="font-manrope text-sm text-white/40 mt-2">{orders.length} order{orders.length === 1 ? "" : "s"} in fulfillment</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 font-manrope text-sm transition-colors" data-testid="fc-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : orders.length === 0 ? (
        <div className="bg-[#141822] border border-white/5 rounded-2xl p-12 text-center font-manrope text-white/40">
          <Package2 className="w-8 h-8 mx-auto mb-3 text-white/20" strokeWidth={1.5} />
          No orders to fulfill right now.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {orders.map((o) => {
            const next = NEXT_STATUS[o.status];
            const review = o.vet_review;
            return (
              <div key={o.order_id} className="bg-[#141822] border border-white/5 rounded-2xl p-5" data-testid={`fc-order-${o.order_id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-outfit font-semibold text-base">{o.medication?.name}</p>
                    <p className="font-manrope text-xs text-white/40 mt-0.5">{o.medication?.dosage} · Quantity {qtyDisplay(o.medication)}</p>
                    <p className="font-manrope text-xs text-white/40 mt-0.5">Order {o.order_id?.slice(0, 8)}…</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${STATUS_BADGE[o.status] || "bg-white/5 text-white/40 border-white/10"}`}>{o.status}</span>
                </div>

                <div className="border-t border-white/5 pt-4 grid grid-cols-2 gap-y-2 font-manrope text-sm">
                  <span className="text-white/40">Customer</span><span className="truncate">{o.customer_name || "n/a"}</span>
                  <span className="text-white/40">Pet</span><span>{o.pet?.name || "n/a"} ({o.pet?.species || "pet"})</span>
                  <span className="text-white/40">Ship to</span>
                  <span className="text-right">{o.shipping_address?.city}, {o.shipping_address?.state_province}</span>
                  {o.tracking?.number && (<>
                    <span className="text-white/40">Tracking</span>
                    <span className="text-right text-cyan-300 truncate">{o.tracking.carrier} {o.tracking.number}</span>
                  </>)}
                </div>

                <button onClick={() => setDetails(o)} className="w-full mt-3 h-9 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg font-manrope text-xs font-medium flex items-center justify-center gap-2 transition-colors" data-testid={`fc-details-${o.order_id}`}>
                  <Eye className="w-3.5 h-3.5" /> View full order details
                </button>

                {review && (
                  <div className="border-t border-white/5 mt-4 pt-3 flex items-center gap-3" data-testid={`fc-vet-cosign-${o.order_id}`}>
                    <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-manrope text-xs text-white/60">Co-signed by {review.vet_name || "vet"}</p>
                      <p className="font-manrope text-xs text-white/40 truncate">License {review.vet_license_number || "n/a"}{review.vet_license_state ? ` · ${review.vet_license_state}` : ""}</p>
                    </div>
                    {review.signature && (
                      <button onClick={() => setViewing({ ...review, order_id: o.order_id })} className="px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-manrope flex items-center gap-1" data-testid={`fc-view-cosign-${o.order_id}`}>
                        <ClipboardCheck className="w-3 h-3" /> View
                      </button>
                    )}
                  </div>
                )}

                {next && (
                  <button onClick={() => advance(o, next)} className="w-full mt-4 h-10 bg-sky-500 hover:bg-sky-400 text-[#0d1118] rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid={`fc-advance-${o.order_id}`}>
                    {next === "shipped" ? <Truck className="w-4 h-4" /> : next === "delivered" || next === "completed" ? <CheckCircle2 className="w-4 h-4" /> : <Package2 className="w-4 h-4" />}
                    Mark as {next}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <form onSubmit={submitTracking} className="bg-[#141822] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="fc-ship-form">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-outfit font-bold text-xl">Mark as Shipped</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1 hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="font-manrope text-xs text-white/60 uppercase tracking-wider">Carrier</label>
                <select value={form.tracking_carrier} onChange={(e) => setForm({ ...form, tracking_carrier: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-sky-400" data-testid="fc-carrier">
                  {CARRIERS.map((c) => <option key={c} value={c}>{c || "Select…"}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="font-manrope text-xs text-white/60 uppercase tracking-wider">Tracking Number</label>
                <input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} required className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-sky-400" data-testid="fc-tracking" />
              </div>
              <div className="space-y-1">
                <label className="font-manrope text-xs text-white/60 uppercase tracking-wider">Notes (optional)</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-sky-400" data-testid="fc-notes" />
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full mt-5 h-11 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-[#0d1118] rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="fc-confirm-ship">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Truck className="w-4 h-4" /> Mark Shipped</>}
            </button>
          </form>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setViewing(null)} data-testid="fc-cosign-modal">
          <div className="bg-[#141822] border border-white/10 rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-outfit font-bold text-xl">Vet Co-sign</h3>
              <button onClick={() => setViewing(null)} className="p-1 hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <dl className="grid grid-cols-2 gap-y-2 font-manrope text-sm mb-4">
              <dt className="text-white/40">Veterinarian</dt><dd>{viewing.vet_name || "n/a"}</dd>
              <dt className="text-white/40">License #</dt><dd>{viewing.vet_license_number || "n/a"}</dd>
              <dt className="text-white/40">State</dt><dd>{viewing.vet_license_state || "n/a"}</dd>
              <dt className="text-white/40">Reviewed</dt><dd>{viewing.reviewed_at?.slice(0, 19).replace("T", " ")}</dd>
            </dl>
            {viewing.signature && (
              <div>
                <p className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2">Signature</p>
                <img src={viewing.signature} alt="vet signature" className="bg-white rounded-xl p-3 w-full" />
              </div>
            )}
          </div>
        </div>
      )}
      {details && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-2 sm:px-6 py-6" onClick={() => setDetails(null)} data-testid="fc-details-modal">
          <div className="bg-[#141822] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#141822] border-b border-white/10 px-5 sm:px-6 py-4 flex justify-between items-center z-10">
              <div>
                <h3 className="font-outfit font-bold text-lg sm:text-xl">Order #{details.order_number || details.order_id?.slice(0, 8)}</h3>
                <p className="font-outfit font-bold text-base sm:text-lg text-white mt-1.5">{details.medication?.name}</p>
                <p className="font-manrope text-sm text-white/70 mt-0.5">{details.medication?.dosage} · <span className="text-white font-semibold">Quantity {qtyDisplay(details.medication)}</span></p>
              </div>
              <button onClick={() => setDetails(null)} className="p-1.5 hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 sm:p-6 space-y-5">
              {/* Customer */}
              <div>
                <p className="font-manrope text-[11px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5"><User className="w-3 h-3" /> Customer</p>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 font-manrope text-sm">
                  <div className="text-white/40 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Name</div><div>{details.customer_name || "n/a"}</div>
                  <div className="text-white/40 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</div><div className="truncate">{details.customer_email || "n/a"}</div>
                  <div className="text-white/40 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Phone</div><div>{details.customer_phone || "n/a"}</div>
                </div>
              </div>

              {/* Pet */}
              <div>
                <p className="font-manrope text-[11px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5"><PawPrint className="w-3 h-3" /> Patient</p>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 font-manrope text-sm">
                  <div className="text-white/40">Name</div><div>{details.pet?.name || "n/a"}</div>
                  <div className="text-white/40">Species</div><div>{details.pet?.species || "n/a"}{details.pet?.breed ? ` · ${details.pet.breed}` : ""}</div>
                  <div className="text-white/40 flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" /> Date of birth</div><div>{details.pet?.dob || "n/a"}</div>
                  {(details.pet?.weight || details.pet?.age) && (<>
                    <div className="text-white/40">Weight / Age</div>
                    <div>
                      {details.pet?.weight ? `${details.pet.weight} ${details.pet.weight_unit || ""}` : "n/a"}
                      {details.pet?.age ? ` · ${details.pet.age} ${details.pet.age_unit || ""}` : ""}
                    </div>
                  </>)}
                  {details.pet?.medical_notes && (<>
                    <div className="text-white/40">Notes</div><div className="italic text-white/80">{details.pet.medical_notes}</div>
                  </>)}
                </div>
              </div>

              {/* Ship-to */}
              <div>
                <p className="font-manrope text-[11px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Ship to</p>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 font-manrope text-sm leading-relaxed">
                  {details.shipping_address ? (
                    <>
                      {details.shipping_address.street}{details.shipping_address.apt ? `, Apt ${details.shipping_address.apt}` : ""}<br />
                      {details.shipping_address.city}, {details.shipping_address.state_province} {details.shipping_address.postal_code}<br />
                      {details.shipping_address.country}
                    </>
                  ) : "n/a"}
                </div>
              </div>

              {/* Prescription with composited signature */}
              <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <p className="font-manrope text-[11px] uppercase tracking-wider text-white/40 flex items-center gap-1.5"><ClipboardCheck className="w-3 h-3" /> Prescription (signed by vet)</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        const u = `${BACKEND_URL}/api/orders/${details.order_id}/prescription-image?t=${encodeURIComponent(details.updated_at || "")}`;
                        try {
                          const res = await fetch(u, { credentials: "include" });
                          const blob = await res.blob();
                          const a = document.createElement("a");
                          a.href = URL.createObjectURL(blob);
                          a.download = `prescription-${details.order_number || details.order_id}.jpg`;
                          document.body.appendChild(a); a.click(); a.remove();
                          setTimeout(() => URL.revokeObjectURL(a.href), 1500);
                        } catch (e) { window.open(u, "_blank"); }
                      }}
                      className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-manrope font-semibold transition-colors"
                      data-testid="fc-download-prescription"
                    ><Download className="w-3.5 h-3.5" /> Download</button>
                    <button
                      onClick={() => {
                        const u = `${BACKEND_URL}/api/orders/${details.order_id}/prescription-image?t=${encodeURIComponent(details.updated_at || "")}`;
                        const w = window.open("", "_blank");
                        if (!w) return;
                        w.document.write(`<html><head><title>Prescription ${details.order_number || details.order_id}</title></head><body style="margin:0;text-align:center"><img src="${u}" style="max-width:100%" onload="setTimeout(function(){window.focus();window.print();},300)"/></body></html>`);
                        w.document.close();
                      }}
                      className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-manrope font-semibold transition-colors"
                      data-testid="fc-print-prescription"
                    ><Printer className="w-3.5 h-3.5" /> Print</button>
                  </div>
                </div>
                <div className="bg-white rounded-xl overflow-auto max-h-[70vh] border border-white/10">
                  {details.prescription_path ? (
                    (!details.signed_prescription_path && /\.pdf$/i.test(details.prescription_filename || details.prescription_path || "")) ? (
                      <iframe
                        title="Prescription"
                        src={`${BACKEND_URL}/api/orders/${details.order_id}/prescription-image?t=${encodeURIComponent(details.updated_at || "")}`}
                        className="w-full h-[70vh] bg-white"
                        data-testid="fc-prescription-pdf"
                      />
                    ) : (
                      <img
                        src={`${BACKEND_URL}/api/orders/${details.order_id}/prescription-image?t=${encodeURIComponent(details.updated_at || "")}`}
                        alt="Prescription"
                        className="w-full bg-white"
                        data-testid="fc-prescription-image"
                      />
                    )
                  ) : (
                    <div className="text-paw-text-muted font-manrope text-sm p-8 text-center">No prescription uploaded.</div>
                  )}
                  {details.prescription_path && (
                    <div className="border-t border-black/10 px-4 py-2 bg-white text-center">
                      <a href={`${BACKEND_URL}/api/orders/${details.order_id}/prescription-image?t=${encodeURIComponent(details.updated_at || "")}`} target="_blank" rel="noreferrer" className="font-manrope text-xs text-sky-700 underline" data-testid="fc-prescription-open">Open full prescription in new tab</a>
                    </div>
                  )}
                  {/* Vet signature, name, license and signed date are baked under each page of the prescription image above */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
